import { useState } from 'react';

/**
 * Utilities for dealing with query params in the URL:
 *
 * ```
 * // Get the query param
 * const value = getQueryParam('some_name');
 * // Set the query param (without redirect)
 * replaceQueryParam('some_name', 'new value');
 *
 *
 * // Use in a react component, input box linked to param
 * const MyComponent = () => {
 *   const [value, setValue] = useQueryParam('some_name');
 *
 *   <input type="text" value={value} onChange={e => setValue(e.target.value)} />
 * }
 * ```
 */

/**
 * Replace a single query parameter in the window url
 *
 * @param {string} name
 * @param {string | null} value
 */
export function replaceQueryParam(name, value) {
  replaceQueryParams({ [name]: value });
}

/**
 * Replace the given set of query paramters in the window url. Values set to
 * null will be removed.
 *
 * @param {Record<string, string | null>} replacementParams
 */
export function replaceQueryParams(replacementParams) {
  const queryParams = new URLSearchParams(window.location.search);
  Object.entries(replacementParams).forEach(([name, value]) => {
    if (value === null) {
      return queryParams.delete(name);
    } else {
      return queryParams.set(name, value);
    }
  });
  const queryParamString = queryParams.size === 0 ? '' : `?${queryParams}`;
  const newUrl = `${window.location.pathname}${queryParamString}${window.location.hash}`;
  window.history.replaceState(
    { turbolinks: true, url: newUrl },
    document.title,
    newUrl
  );
}

/**
 * Get a param from the current location query
 *
 * @param {string} name
 * @returns {string|null}
 */
export function getQueryParam(name) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(name);
}

/**
 * Get a value from the query paramters and keep it up to date with new state
 * values
 *
 * @param {string} name
 * @returns {[string|null, import('react').Dispatch<import('react').SetStateAction<string>>]}
 */
export function useQueryParam(name) {
  const [param, setParam] = useState(() => getQueryParam(name));

  const setter = newValue => {
    const value = typeof newValue === 'function' ? newValue() : newValue;
    setParam(value);
    replaceQueryParam(name, value);
  };

  return [param, setter];
}
